import {useCallback} from 'react';

import {assertPolicyFilter} from 'filters/types';
import {StoredFilterType} from 'modules/decisionsNext/api/types';
import {UseStoredFilters} from 'filters/features/types';

import {PageFilter} from 'filters';
import {cleanEmptyValuesStrict} from 'utils/cleanEmptyValues';
import {EnnablLogger} from 'utils/ennabl';
import {clearFilter} from 'filters/utils';
import {pushPolicyFilterToRecent} from 'features/GlobalFilterProvider/utils/cache';
import {useCypressFilterApply} from 'filters/hooks/useCypressFilterOverride';
import {useUpsertPolicyFilterMutation} from 'features/GlobalFilterProvider/api/policyFilterMutations.generated';
import {useUpdateFilterLastUsedMutation} from 'api/filters/filterMutations.generated';
import {PolicyFilterTag} from 'constants/policyFilterTags';
import {RecentPolicyFiltersDocument} from 'features/GlobalFilterProvider/api/policyFilters.generated';
import {useGlobalStoredFilters} from 'features/GlobalFilterProvider/hooks/useGlobalStoredFilters';

import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

export const createPolicyFilterApply = (tag: PolicyFilterTag, useStoredFilters: UseStoredFilters) => () => {
  const {
    savedFilters: {savedWithDefault},
    recentFilters,
  } = useStoredFilters();
  const [upsertPolicyFilter] = useUpsertPolicyFilterMutation();
  const [updateLastUsed] = useUpdateFilterLastUsedMutation();

  return useCypressFilterApply(
    useCallback(
      async (filter: PageFilter) => {
        assertPolicyFilter(filter);

        const clearedFilter = clearFilter(cleanEmptyValuesStrict(filter) as PageFilter);

        const savedFilter = savedWithDefault?.find(storedSaved =>
          isEqualFilters(clearFilter(storedSaved.filter), clearedFilter)
        );
        const storedFilter =
          savedFilter ||
          recentFilters?.find(storedRecent => isEqualFilters(clearFilter(storedRecent.filter), clearedFilter));
        const isNew = !storedFilter;

        EnnablLogger.log(['PolicyFilterApply', tag], {
          isNew,
          storedFilter,
          clearedFilter,
          savedFilter,
        });

        if (isNew) {
          return void upsertPolicyFilter({
            variables: {
              input: {
                tag,
                type: StoredFilterType.Recent,
                filter: clearedFilter,
              },
            },
            refetchQueries: [
              {
                query: RecentPolicyFiltersDocument,
                variables: {tag},
              },
            ],
            optimisticResponse: {
              upsertPolicyFilter: {
                tag,
                id: 'temp-id',
                name: 'Recent Filter',
                lastUsed: new Date().toISOString(),
                type: StoredFilterType.Recent,
              },
            },
            update(cache) {
              pushPolicyFilterToRecent({
                tag,
                cache,
                type: StoredFilterType.Recent,
                filter: {
                  id: 'temp-id',
                  name: 'Recent Filter',
                  filter,
                },
              });
            },
          });
        }

        return void updateLastUsed({
          variables: {
            id: storedFilter.id,
          },
          refetchQueries: [
            {
              query: RecentPolicyFiltersDocument,
              variables: {tag},
            },
          ],
          optimisticResponse: {
            updateFilterLastUsed: {
              success: true,
            },
          },
          update(cache) {
            pushPolicyFilterToRecent({
              tag,
              cache,
              filter: storedFilter,
              type: savedFilter ? StoredFilterType.Saved : StoredFilterType.Recent,
            });
          },
        });
      },
      [recentFilters, savedWithDefault, updateLastUsed, upsertPolicyFilter]
    )
  );
};

export const useGlobalFilterApply = createPolicyFilterApply(PolicyFilterTag.GLOBAL, useGlobalStoredFilters);
